/**
 * Invisible Noto Sans script fallbacks (not in the font selector).
 * Embed only when Unicode ranges appear in the document.
 */

export type ScriptFallbackKey =
  | "arabic"
  | "bengali"
  | "devanagari"
  | "thai"
  | "hangul"
  | "kana"
  | "han";

type UnicodeRange = readonly [start: number, end: number];

export type ScriptFallbackDefinition = {
  /** Stable id used in detection, URLs, and stacks. */
  key: ScriptFallbackKey;
  /** Directory under public/fonts/ and @fontsource package suffix. */
  fontDir: string;
  family: string;
  /** @fontsource subset name for the script glyphs. */
  subset: string;
  ranges: readonly UnicodeRange[];
};

export const SCRIPT_FALLBACK_FONTS: readonly ScriptFallbackDefinition[] = [
  {
    key: "arabic",
    fontDir: "noto-sans-arabic",
    family: "Noto Sans Arabic",
    subset: "arabic",
    ranges: [
      [0x0600, 0x06ff],
      [0x0750, 0x077f],
      [0x08a0, 0x08ff],
      [0xfb50, 0xfdff],
      [0xfe70, 0xfeff],
    ],
  },
  {
    key: "bengali",
    fontDir: "noto-sans-bengali",
    family: "Noto Sans Bengali",
    subset: "bengali",
    ranges: [[0x0980, 0x09ff]],
  },
  {
    key: "devanagari",
    fontDir: "noto-sans-devanagari",
    family: "Noto Sans Devanagari",
    subset: "devanagari",
    ranges: [[0x0900, 0x097f]],
  },
  {
    key: "thai",
    fontDir: "noto-sans-thai",
    family: "Noto Sans Thai",
    subset: "thai",
    ranges: [[0x0e00, 0x0e7f]],
  },
  {
    key: "hangul",
    fontDir: "noto-sans-kr",
    family: "Noto Sans KR",
    subset: "korean",
    ranges: [
      [0x1100, 0x11ff],
      [0x3130, 0x318f],
      [0xa960, 0xa97f],
      [0xac00, 0xd7af],
      [0xd7b0, 0xd7ff],
    ],
  },
  {
    key: "kana",
    fontDir: "noto-sans-jp",
    family: "Noto Sans JP",
    subset: "japanese",
    ranges: [
      [0x3040, 0x309f],
      [0x30a0, 0x30ff],
      [0x31f0, 0x31ff],
      [0xff66, 0xff9d],
    ],
  },
  {
    key: "han",
    fontDir: "noto-sans-sc",
    family: "Noto Sans SC",
    subset: "chinese-simplified",
    ranges: [
      [0x3400, 0x4dbf],
      [0x4e00, 0x9fff],
      [0xf900, 0xfaff],
    ],
  },
];

const SCRIPT_FALLBACK_BY_KEY = new Map(
  SCRIPT_FALLBACK_FONTS.map((font) => [font.key, font]),
);

export function isScriptFallbackKey(
  value: unknown,
): value is ScriptFallbackKey {
  return typeof value === "string" && SCRIPT_FALLBACK_BY_KEY.has(value as ScriptFallbackKey);
}

export function getScriptFallbackDefinition(
  key: ScriptFallbackKey,
): ScriptFallbackDefinition {
  const font = SCRIPT_FALLBACK_BY_KEY.get(key);

  if (!font) {
    throw new Error(`Unknown script fallback key: ${key}.`);
  }

  return font;
}

function codePointInRanges(
  codePoint: number,
  ranges: readonly UnicodeRange[],
): boolean {
  for (const [start, end] of ranges) {
    if (codePoint >= start && codePoint <= end) {
      return true;
    }
  }

  return false;
}

/**
 * Scan markdown (or any text) for script ranges that need Noto fallbacks.
 * CJK rule: kana → JP only (drop han); Hangul → KR; Han without kana → SC.
 */
export function detectNeededScriptFallbacks(
  text: string,
): ScriptFallbackKey[] {
  const found = new Set<ScriptFallbackKey>();
  const pending = new Set<ScriptFallbackKey>(
    SCRIPT_FALLBACK_FONTS.map((font) => font.key),
  );

  for (let index = 0; index < text.length && pending.size > 0; index += 1) {
    const codePoint = text.codePointAt(index);

    if (codePoint === undefined) {
      continue;
    }

    if (codePoint > 0xffff) {
      index += 1;
    }

    for (const font of SCRIPT_FALLBACK_FONTS) {
      if (!pending.has(font.key)) {
        continue;
      }

      if (!codePointInRanges(codePoint, font.ranges)) {
        continue;
      }

      found.add(font.key);
      pending.delete(font.key);

      // JP covers kanji; once kana is seen, never need SC for this doc.
      if (font.key === "kana") {
        pending.delete("han");
        found.delete("han");
      }

      break;
    }
  }

  return SCRIPT_FALLBACK_FONTS.map((font) => font.key).filter((key) =>
    found.has(key),
  );
}

/** Parse `scripts=bengali,arabic` (script ids) from fonts.css query. */
export function parseScriptFallbackKeys(
  value: string | null | undefined,
): ScriptFallbackKey[] {
  if (!value) {
    return [];
  }

  const seen = new Set<ScriptFallbackKey>();

  for (const part of value.split(",")) {
    const trimmed = part.trim().toLowerCase();

    if (isScriptFallbackKey(trimmed)) {
      seen.add(trimmed);
    }
  }

  return SCRIPT_FALLBACK_FONTS.map((font) => font.key).filter((key) =>
    seen.has(key),
  );
}

export function serializeScriptFallbacks(
  scripts: readonly ScriptFallbackKey[],
): string {
  return SCRIPT_FALLBACK_FONTS.map((font) => font.key)
    .filter((key) => scripts.includes(key))
    .join(",");
}
